/**
 * BannedGate — bloqueo global cuando `publicMetadata.banned === true`.
 *
 * Se monta dentro del `<ClerkProvider>` y arriba de las rutas. Si el claim
 * `banned` del session JWT (o publicMetadata) es true, el usuario ve una
 * pantalla bloqueante con un botón para cerrar sesión. Sin sesión Clerk no
 * hay banned, así que la app pública sigue navegable.
 *
 * Productor del flag: api/admin/notify-pending-subscriber.ts (al exceder
 * el threshold de spam) o un admin desde el dashboard Clerk.
 */

import { useClerk } from "@clerk/clerk-react";
import { ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";
import { useIsBanned } from "@/lib/auth";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import { useClerkGateMounted } from "@/lib/clerkGate";

interface BannedGateProps {
  children: ReactNode;
}

// Constante de build: sin publishable key no se monta <ClerkProvider> (ver
// AppClerkProvider en App.tsx), por lo que los hooks de Clerk lanzarían. En ese
// caso el sitio público debe seguir navegable: no hay sesión, no hay banned.
const CLERK_ENABLED = Boolean(getClerkPublishableKey());

export function BannedGate({ children }: BannedGateProps) {
  // Gate dinámico: en la landing el provider se difiere (perf mobile) —
  // sin Clerk montado no hay sesión, ergo no hay banned.
  const clerkMounted = useClerkGateMounted();
  if (!CLERK_ENABLED || !clerkMounted) return <>{children}</>;
  return <BannedGateClerk>{children}</BannedGateClerk>;
}

function BannedGateClerk({ children }: BannedGateProps) {
  const banned = useIsBanned();
  if (!banned) return <>{children}</>;
  return <BannedScreen />;
}

function BannedScreen() {
  const { signOut } = useClerk();
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-6 py-12 text-zinc-100">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15 ring-1 ring-red-500/30 mx-auto">
          <ShieldAlert className="h-8 w-8 text-red-400" aria-hidden />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Cuenta bloqueada
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Tu cuenta fue marcada por uso indebido. No podés acceder al
            contenido privado. Si creés que es un error, escribinos desde
            otro email y revisamos el caso.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void signOut()}
          className="inline-flex items-center justify-center rounded-md bg-zinc-800 hover:bg-zinc-700 px-4 py-2 text-sm font-medium text-zinc-100 ring-1 ring-zinc-700 transition-colors"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
